import { useState, useEffect, useRef } from "react";
import { track as vercelTrack } from "@vercel/analytics";
import { useAuth } from "../../contexts/AuthContext";
import { track as phTrack } from "../../lib/posthog";

/* ── Palette ─────────────────────────────────────────────────── */
const C = {
  forest: "#143D2E",
  forestLight: "#1B5E3F",
  cream: "#FDF8F0",
  creamDark: "#F2E8D8",
  white: "#FFFFFF",
  blue: "#3B4FA8",
  orange: "#C65D3B",
  orangeHover: "#A84D30",
  text: "#1A1A1A",
  textSec: "#5C5C5C",
  textInv: "#FDF8F0",
  success: "#2D8F5E",
  divider: "#E0D5C4",
  footerBg: "#0E2B20",
};

const FF = {
  display: "'Fraunces', serif",
  body: "'DM Sans', sans-serif",
  mono: "'JetBrains Mono', monospace",
};

/* ── Scroll-triggered fade-in hook ───────────────────────────── */
function useFadeIn() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) { setVisible(true); return; }
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const style = {
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(32px)",
    transition: "opacity 0.6s cubic-bezier(0.16,1,0.3,1), transform 0.6s cubic-bezier(0.16,1,0.3,1)",
  };
  return { ref, style };
}

/* ── SVG Icons (inline, no emojis) ───────────────────────────── */
const Icon = {
  wallet: (color = C.orange) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="20" height="14" rx="3" /><path d="M2 10h20" /><circle cx="17" cy="15" r="1" fill={color} />
    </svg>
  ),
  cards: (color = C.orange) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="16" height="12" rx="2" /><rect x="7" y="8" width="16" height="12" rx="2" />
    </svg>
  ),
  clock: (color = C.orange) => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
    </svg>
  ),
  receipt: (color = C.forest) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 3 2V2L19 4 16 2 13 4 10 2 7 4Z" /><path d="M8 10h8" /><path d="M8 14h5" />
    </svg>
  ),
  bank: (color = C.forest) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18" /><path d="M3 10h18" /><path d="M12 3l9 7H3z" /><path d="M5 10v11" /><path d="M19 10v11" /><path d="M9 10v11" /><path d="M15 10v11" />
    </svg>
  ),
  calendar: (color = C.forest) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="3" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" />
    </svg>
  ),
  target: (color = C.forest) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" fill={color} />
    </svg>
  ),
  chart: (color = C.forest) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 20h18" /><rect x="5" y="12" width="4" height="8" rx="1" /><rect x="10" y="8" width="4" height="12" rx="1" /><rect x="15" y="4" width="4" height="16" rx="1" />
    </svg>
  ),
  repeat: (color = C.forest) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
  ),
  mic: (color = C.forest) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="2" width="6" height="11" rx="3" /><path d="M5 10a7 7 0 0 0 14 0" /><path d="M12 19v4" />
    </svg>
  ),
  brain: (color = C.forest) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a6 6 0 0 0-6 6c0 2 1 3.5 2.5 4.5L12 22l3.5-9.5C17 11.5 18 10 18 8a6 6 0 0 0-6-6z" /><circle cx="12" cy="8" r="2" />
    </svg>
  ),
  bell: (color = C.forest) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  ),
  check: (color = C.success) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  dash: (color = C.textSec) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  plus: (color = C.forest) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  shield: (color = C.forest) => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l8 4v6c0 5-3.5 9.5-8 10-4.5-.5-8-5-8-10V6l8-4z"/>
    </svg>
  ),
};

/* ── Media query helper ──────────────────────────────────────── */
function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return mobile;
}

/* ═══════════════════════════════════════════════════════════════
   LANDING PAGE — Componente principal
   ═══════════════════════════════════════════════════════════════ */
export default function LandingPage() {
  const { signInWithGoogle } = useAuth();
  const mobile = useIsMobile();
  const [navScrolled, setNavScrolled] = useState(false);
  const [faqOpen, setFaqOpen] = useState(new Array(8).fill(false));

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Wrapper que trackea el origen del click antes de iniciar el login
  const handleLogin = (opts = {}) => {
    const source = typeof opts === "string" ? opts : (opts.source || "unknown");
    const plan = typeof opts === "object" ? opts.plan : null;
    vercelTrack("signup_click", { location: source, plan: plan || undefined });
    phTrack("landing_signup_click", { location: source, plan: plan || undefined });
    return signInWithGoogle({ plan });
  };

  const toggleFaq = (i) => {
    setFaqOpen(prev => prev.map((v, idx) => idx === i ? !v : v));
    // Solo trackear cuando se abre, no cuando se cierra
    if (!faqOpen[i]) {
      vercelTrack("faq_open", { index: i });
      phTrack("landing_faq_open", { index: i });
    }
  };

  return (
    <div style={{ background: C.cream, color: C.text, fontFamily: FF.body, WebkitFontSmoothing: "antialiased" }}>

      {/* ── NAV ── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 50, height: mobile ? 56 : 64,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: mobile ? "0 20px" : "0 60px",
        background: navScrolled ? "rgba(253,248,240,0.85)" : "transparent",
        backdropFilter: navScrolled ? "blur(12px)" : "none",
        WebkitBackdropFilter: navScrolled ? "blur(12px)" : "none",
        borderBottom: navScrolled ? `1px solid ${C.divider}` : "1px solid transparent",
        transition: "all 0.3s ease",
      }}>
        <span style={{ fontFamily: FF.display, fontWeight: 700, fontSize: 24, color: C.forest }}>arbo</span>
        {!mobile && (
          <div style={{ display: "flex", gap: 32 }}>
            {[["Funciones", "features"], ["Duky", "duky"], ["Precios", "precios"]].map(([label, anchor]) => (
              <a key={label} href={`#${anchor}`} style={{ fontFamily: FF.body, fontWeight: 500, fontSize: 15, color: C.forest, textDecoration: "none" }}>{label}</a>
            ))}
          </div>
        )}
        <button onClick={() => handleLogin("nav")} style={{
          padding: "10px 20px", borderRadius: 12, border: `1.5px solid ${C.forest}`, background: "transparent",
          color: C.forest, fontFamily: FF.body, fontWeight: 700, fontSize: 14, cursor: "pointer",
          transition: "all 0.2s",
        }}>
          Empezar gratis
        </button>
      </nav>

      {/* ── HERO ── */}
      <section style={{
        minHeight: "100svh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        textAlign: "center", padding: mobile ? "100px 20px 60px" : "120px 60px 80px", position: "relative", overflow: "hidden",
      }}>
        <div style={{ opacity: 0, animation: "fadeUp 0.6s 0s forwards" }}>
          <div style={{ fontFamily: FF.body, fontWeight: 500, fontSize: 14, textTransform: "uppercase", letterSpacing: "0.12em", color: C.blue, marginBottom: 16 }}>
            TUS FINANZAS PERSONALES
          </div>
        </div>
        <h1 style={{ opacity: 0, animation: "fadeUp 0.6s 0.15s forwards", fontFamily: FF.display, fontWeight: 700, fontSize: mobile ? 40 : 72, lineHeight: 1.1, color: C.forest, margin: "0 0 20px", maxWidth: 800 }}>
          Tus finanzas claras.{mobile ? " " : <br />}Tu vida, m&aacute;s tranquila.
        </h1>
        <p style={{ opacity: 0, animation: "fadeUp 0.6s 0.3s forwards", fontFamily: FF.body, fontSize: mobile ? 17 : 20, color: C.textSec, maxWidth: 560, margin: "0 auto 32px", lineHeight: 1.6 }}>
          Arbo te ayuda a registrar gastos, controlar tus tarjetas y alcanzar tus metas de ahorro &mdash; sin hojas de c&aacute;lculo, sin complicaciones.
        </p>
        <div style={{ opacity: 0, animation: "fadeUp 0.6s 0.45s forwards", display: "flex", flexDirection: mobile ? "column" : "row", gap: 12, alignItems: "center" }}>
          <button onClick={() => {
            try { phTrack("landing_cta_click", { source: "hero" }); } catch {}
            document.getElementById("precios")?.scrollIntoView({ behavior: "smooth" });
          }} style={{
            padding: "16px 32px", borderRadius: 16, border: "none", background: C.orange, color: C.white,
            fontFamily: FF.body, fontWeight: 700, fontSize: 16, cursor: "pointer", boxShadow: "0 4px 20px rgba(198,93,59,0.3)",
            transition: "all 0.2s",
          }}>
            Empieza ahora
          </button>
          <a href="#features" style={{ fontFamily: FF.body, fontWeight: 600, fontSize: 15, color: C.forest, textDecoration: "underline", textUnderlineOffset: 4 }}>
            Ve c&oacute;mo funciona &darr;
          </a>
        </div>
        <div style={{ opacity: 0, animation: "fadeUp 0.6s 0.55s forwards", fontFamily: FF.body, fontSize: 14, color: C.textSec, marginTop: 12 }}>
          Sin tarjeta. Cancela cuando quieras.
        </div>

        {/* Mockup placeholder */}
        <div style={{
          opacity: 0, animation: "fadeUp 0.8s 0.65s forwards",
          marginTop: 48, width: mobile ? 220 : 280, aspectRatio: "9/19",
          background: C.creamDark, border: "8px solid #1E293B", borderRadius: 32,
          boxShadow: "0 24px 80px rgba(20,61,46,0.15)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: FF.body, fontSize: 14, color: C.textSec, fontWeight: 500,
        }}>
          App Preview
        </div>
      </section>

      {/* ── PROBLEMA ── */}
      <SectionProblem />

      {/* ── CÓMO FUNCIONA ── */}
      <SectionHowItWorks mobile={mobile} />

      {/* ── FEATURES ── */}
      <SectionFeatures mobile={mobile} />

      {/* ── DUKY ── */}
      <SectionDuky mobile={mobile} />

      {/* ── SOCIAL PROOF ── */}
      <SectionProof mobile={mobile} />

      {/* ── PRECIOS ── */}
      <SectionPricing mobile={mobile} onLogin={(opts) => handleLogin({ source: "pricing", ...opts })} />

      {/* ── SEGURIDAD ── */}
      <SectionSecurity mobile={mobile} />

      {/* ── FAQ ── */}
      <SectionFAQ faqOpen={faqOpen} toggleFaq={toggleFaq} />

      {/* ── CTA FINAL ── */}
      <SectionCTA mobile={mobile} onLogin={() => handleLogin("cta_final")} />

      {/* ── FOOTER ── */}
      <Footer mobile={mobile} />

      {/* ── Global keyframes ── */}
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(24px); }
            to { opacity: 1; transform: translateY(0); }
          }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes fadeUp {
            from { opacity: 1; transform: none; }
            to { opacity: 1; transform: none; }
          }
        }
        html { scroll-behavior: smooth; }
      `}</style>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════
   SECCIONES
   ═══════════════════════════════════════════════════════════════ */

function SectionProblem() {
  const fade = useFadeIn();
  const painPoints = [
    { icon: Icon.wallet, text: "Llegas a quincena sin saber a donde se fue tu dinero." },
    { icon: Icon.cards, text: "Tienes 3 tarjetas, 2 apps de banco y 0 control real." },
    { icon: Icon.clock, text: 'Cada mes dices "ahora si voy a ahorrar" y no pasa.' },
  ];
  return (
    <section ref={fade.ref} style={{ ...fade.style, background: C.forest, padding: "100px 20px", textAlign: "center" }}>
      <div style={{ maxWidth: 720, margin: "0 auto", display: "flex", flexDirection: "column", gap: 28 }}>
        {painPoints.map((p, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 16, justifyContent: "center" }}>
            <div style={{ flexShrink: 0 }}>{p.icon()}</div>
            <span style={{ fontFamily: FF.body, fontWeight: 500, fontSize: 18, color: C.textInv, textAlign: "left" }}>{p.text}</span>
          </div>
        ))}
        <div style={{ width: 64, height: 1, background: C.forestLight, margin: "12px auto" }} />
        <p style={{ fontFamily: FF.display, fontStyle: "italic", fontWeight: 700, fontSize: 24, color: C.textInv, lineHeight: 1.4, maxWidth: 600, margin: "0 auto" }}>
          No es que seas malo con el dinero.<br />Es que nadie te dio las herramientas correctas.
        </p>
      </div>
    </section>
  );
}

/* Sección: cómo funciona en 4 pasos */
function SectionHowItWorks({ mobile }) {
  const fade = useFadeIn();
  const steps = [
    {
      num: "01",
      icon: Icon.plus,
      title: "Crea tu cuenta gratis",
      desc: "Solo necesitas tu correo. Menos de 1 minuto.",
    },
    {
      num: "02",
      icon: Icon.bank,
      title: "Agrega tus cuentas y tarjetas",
      desc: "Tú decides cuáles incluir. Débito, crédito, efectivo, lo que uses.",
    },
    {
      num: "03",
      icon: Icon.mic,
      title: "Anota tus gastos",
      desc: "Escríbelos rápido o dícelos a Duky en una nota de voz. Lo que sea más fácil.",
    },
    {
      num: "04",
      icon: Icon.chart,
      title: "Ve a dónde se va tu dinero",
      desc: "Reportes claros mes a mes. Sin Excel, sin complicaciones.",
    },
  ];
  return (
    <section ref={fade.ref} style={{ ...fade.style, background: C.cream, padding: mobile ? "80px 20px" : "120px 60px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
        <h2 style={{ fontFamily: FF.display, fontWeight: 600, fontSize: mobile ? 32 : 48, color: C.forest, margin: "0 0 12px" }}>
          Así de fácil funciona
        </h2>
        <p style={{ fontFamily: FF.body, fontSize: 18, color: C.textSec, margin: "0 0 56px" }}>
          En menos de 5 minutos estás listo para tomar control de tus finanzas.
        </p>
        <div style={{
          display: "grid",
          gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr 1fr",
          gap: 24,
        }}>
          {steps.map((s, i) => (
            <div key={i} style={{
              background: C.white, borderRadius: 24, padding: 32, textAlign: "left",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)",
            }}>
              <div style={{ fontFamily: FF.mono, fontWeight: 700, fontSize: 36, color: C.divider, marginBottom: 16, lineHeight: 1 }}>
                {s.num}
              </div>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: C.creamDark, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                {s.icon()}
              </div>
              <div style={{ fontFamily: FF.display, fontWeight: 600, fontSize: 18, color: C.forest, marginBottom: 8 }}>
                {s.title}
              </div>
              <p style={{ fontFamily: FF.body, fontSize: 15, color: C.textSec, lineHeight: 1.6, margin: 0 }}>
                {s.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionFeatures({ mobile }) {
  const fade = useFadeIn();
  const features = [
    { icon: Icon.receipt, title: "Gastos e ingresos", desc: "Registra cada peso en segundos. Escribiéndolo, dictándolo a Duky, o dejando que se anote solo si es un pago fijo.", span: 2 },
    { icon: Icon.bank, title: "Cuentas y tarjetas", desc: "Ve todos tus saldos juntos. Débito, crédito, efectivo, todo en un solo lugar.", span: 1 },
    { icon: Icon.calendar, title: "Meses sin intereses, sin sorpresas", desc: "Lleva el control de tus compras a meses. Sabes exacto cuánto debes y cuándo terminas.", span: 1 },
    { icon: Icon.target, title: "Metas de ahorro", desc: "Ponle nombre a tus metas. Ve tu progreso crecer cada quincena.", span: 2 },
    { icon: Icon.chart, title: "Presupuestos inteligentes", desc: "Define cuanto puedes gastar por categoria. Arbo te avisa antes de que te pases.", span: 2 },
    { icon: Icon.repeat, title: "Gastos fijos, en automático", desc: "Netflix, Spotify, la renta, se registran solos. Tú no haces nada.", span: 1 },
  ];
  return (
    <section id="features" ref={fade.ref} style={{ ...fade.style, padding: mobile ? "80px 20px" : "120px 60px", background: C.cream }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <h2 style={{ fontFamily: FF.display, fontWeight: 600, fontSize: mobile ? 32 : 48, color: C.forest, textAlign: "center", margin: "0 0 8px" }}>
          Todo en un solo lugar
        </h2>
        <p style={{ fontFamily: FF.body, fontSize: 18, color: C.textSec, textAlign: "center", margin: "0 0 48px" }}>
          Sin cambiar de app. Sin exportar nada. Sin Excel.
        </p>
        <div style={{
          display: "grid",
          gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr",
          gap: 16,
        }}>
          {features.map((f, i) => (
            <div key={i} style={{
              gridColumn: mobile ? "auto" : (f.span === 2 ? "span 2" : "auto"),
              background: C.white, borderRadius: 24, padding: 32,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, background: C.creamDark, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                {f.icon()}
              </div>
              <div style={{ fontFamily: FF.display, fontWeight: 600, fontSize: 22, color: C.forest, marginBottom: 8 }}>{f.title}</div>
              <div style={{ fontFamily: FF.body, fontSize: 16, color: C.textSec, lineHeight: 1.6 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionDuky({ mobile }) {
  const fade = useFadeIn();
  const capabilities = [
    { icon: Icon.mic, text: 'Dile "gaste 850 en el super" y listo. Ella registra todo.' },
    { icon: Icon.bell, text: "Te avisa si vas a rebasar tu presupuesto del mes." },
    { icon: Icon.brain, text: "Analiza tus patrones y te sugiere donde puedes ahorrar." },
  ];
  return (
    <section id="duky" ref={fade.ref} style={{ ...fade.style, background: C.creamDark, padding: mobile ? "80px 20px" : "120px 60px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", flexDirection: mobile ? "column" : "row", gap: mobile ? 40 : 60, alignItems: "center" }}>
        {/* Duky illustration placeholder */}
        <div style={{
          flex: mobile ? "none" : "0 0 50%", width: mobile ? "100%" : "50%",
          aspectRatio: "1", maxWidth: 400, background: C.cream, borderRadius: 32,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          border: `2px solid ${C.divider}`,
        }}>
          <div style={{ fontSize: 80, marginBottom: 12 }}>🐿️</div>
          <div style={{ fontFamily: FF.display, fontWeight: 600, fontSize: 20, color: C.forest }}>Duky</div>
          <div style={{ fontFamily: FF.body, fontSize: 13, color: C.textSec }}>Tu compañera de finanzas</div>
        </div>

        <div style={{ flex: 1 }}>
          <div style={{
            display: "inline-block", padding: "6px 14px", borderRadius: 99, background: C.blue, color: C.white,
            fontFamily: FF.body, fontWeight: 600, fontSize: 12, marginBottom: 16, letterSpacing: "0.02em",
          }}>
            Tu asistente personal con IA
          </div>
          <h2 style={{ fontFamily: FF.display, fontWeight: 600, fontSize: mobile ? 28 : 40, color: C.forest, lineHeight: 1.2, margin: "0 0 20px" }}>
            Duky sabe de finanzas.<br />Y no te va a juzgar.
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 28 }}>
            {capabilities.map((c, i) => (
              <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: C.cream, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {c.icon()}
                </div>
                <p style={{ fontFamily: FF.body, fontSize: 16, color: C.textSec, lineHeight: 1.6, margin: 0 }}>{c.text}</p>
              </div>
            ))}
          </div>

          {/* Chat bubble */}
          <div style={{ background: C.white, borderRadius: 20, padding: 20, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
            <div style={{ fontFamily: FF.body, fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 }}>Tu:</div>
            <div style={{ fontFamily: FF.body, fontSize: 14, color: C.textSec, marginBottom: 12 }}>"Duky, como voy este mes?"</div>
            <div style={{ fontFamily: FF.body, fontSize: 13, fontWeight: 600, color: C.forest, marginBottom: 8 }}>Duky:</div>
            <div style={{ fontFamily: FF.body, fontSize: 14, color: C.textSec, lineHeight: 1.6 }}>
              "Vas bien. Llevas 62% del presupuesto con 40% del mes. Tu categoria mas alta es comida fuera: $2,840 de $4,000."
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionProof({ mobile }) {
  const fade = useFadeIn();
  {/* TODO: reemplazar testimonios sintéticos con reales cuando estén disponibles */}
  const testimonials = [
    { initials: "MR", name: "María Reyes", role: "Diseñadora freelance", quote: "Por fin sé cuánto realmente gano cada mes. Antes era un misterio." },
    { initials: "JL", name: "José López", role: "Papá de familia", quote: "Llegamos al fin de mes con dinero, no debiendo. Eso vale oro." },
    { initials: "FT", name: "Fernanda Torres", role: "Recién egresada", quote: "Ahorrar dejó de ser un misterio. Veo mis metas crecer cada quincena." },
  ];
  return (
    <section ref={fade.ref} style={{ ...fade.style, background: C.forest, padding: mobile ? "80px 20px" : "120px 60px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
        <h2 style={{ fontFamily: FF.display, fontWeight: 600, fontSize: mobile ? 28 : 40, color: C.textInv, margin: "0 0 12px" }}>
          Hecho para personas reales
        </h2>
        <p style={{ fontFamily: FF.body, fontSize: 18, color: "rgba(253,248,240,0.65)", margin: "0 0 48px" }}>
          No para expertos en finanzas. Para ti.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr", gap: 16 }}>
          {testimonials.map((t, i) => (
            <div key={i} style={{
              background: C.forestLight, borderRadius: 20, padding: 32, textAlign: "left",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                {/* Avatar circular con iniciales */}
                <div style={{
                  width: 44, height: 44, borderRadius: "50%", background: C.orange,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: FF.body, fontWeight: 700, fontSize: 15, color: C.white, flexShrink: 0,
                }}>
                  {t.initials}
                </div>
                <div>
                  <div style={{ fontFamily: FF.body, fontWeight: 700, fontSize: 15, color: C.textInv }}>{t.name}</div>
                  <div style={{ fontFamily: FF.body, fontSize: 13, color: "rgba(253,248,240,0.55)" }}>{t.role}</div>
                </div>
              </div>
              <p style={{ fontFamily: FF.display, fontStyle: "italic", fontSize: 17, color: "rgba(253,248,240,0.85)", lineHeight: 1.6, margin: 0 }}>
                &ldquo;{t.quote}&rdquo;
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionPricing({ mobile, onLogin }) {
  const fade = useFadeIn();
  const plans = [
    {
      name: "Gratis", price: "$0", period: "/mes", bg: C.white, color: C.text, borderColor: C.divider,
      badge: null, ctaLabel: "Empezar gratis", ctaBg: C.forest, ctaColor: C.white,
      features: [
        [true, "2 cuentas"], [true, "1 tarjeta de credito"], [true, "5 presupuestos"],
        [true, "1 meta de ahorro"], [true, "30 conversaciones con Duky al mes"], [true, "10 notas de voz al mes"],
        [false, "Categorías personalizadas"], [false, "Control de meses sin intereses"], [false, "Comparar meses anteriores"],
      ],
    },
    {
      name: "Basico", price: "$89", period: "/mes", bg: C.forest, color: C.textInv, borderColor: "transparent",
      badge: "RECOMENDADO", badgeBg: C.orange, ctaLabel: "Elegir Basico", ctaBg: C.orange, ctaColor: C.white,
      scale: true, trial: "15 días gratis · Sin tarjeta",
      features: [
        [true, "8 cuentas"], [true, "5 tarjetas de credito"], [true, "15 presupuestos"],
        [true, "5 metas de ahorro"], [true, "200 conversaciones con Duky al mes"], [true, "60 notas de voz al mes"],
        [true, "15 categorías personalizadas"], [true, "20 compras a meses sin intereses"], [false, "Comparar meses anteriores"],
      ],
    },
    {
      name: "Pro", price: "$119", period: "/mes", bg: C.white, color: C.text, borderColor: C.divider,
      badge: "MAS COMPLETO", badgeBg: C.blue, ctaLabel: "Elegir Pro", ctaBg: C.blue, ctaColor: C.white,
      trial: "15 días gratis · Sin tarjeta",
      features: [
        [true, "15 cuentas"], [true, "10 tarjetas de credito"], [true, "30 presupuestos"],
        [true, "10 metas de ahorro"], [true, "500 conversaciones con Duky al mes"], [true, "150 notas de voz al mes"],
        [true, "30 categorías personalizadas"], [true, "40 compras a meses sin intereses"], [true, "Comparar meses + proyección de gastos"],
      ],
    },
  ];

  return (
    <section id="precios" ref={fade.ref} style={{ ...fade.style, padding: mobile ? "80px 20px" : "120px 60px", background: C.cream }}>
      <div style={{ maxWidth: 1080, margin: "0 auto", textAlign: "center" }}>
        <h2 style={{ fontFamily: FF.display, fontWeight: 600, fontSize: mobile ? 32 : 48, color: C.forest, margin: "0 0 8px" }}>
          Empieza gratis. Crece cuando quieras.
        </h2>
        <p style={{ fontFamily: FF.body, fontSize: 18, color: C.textSec, margin: "0 0 48px" }}>
          Sin compromisos. Sin letra chiquita. Sin cargos sorpresa.
        </p>

        <div style={{
          display: "flex", flexDirection: mobile ? "column" : "row", gap: mobile ? 20 : 16,
          alignItems: mobile ? "stretch" : "center", justifyContent: "center",
        }}>
          {(mobile ? [plans[1], plans[0], plans[2]] : plans).map((plan) => (
            <div key={plan.name} style={{
              flex: mobile ? "none" : "0 0 320px",
              background: plan.bg, color: plan.color,
              border: `1px solid ${plan.borderColor}`,
              borderRadius: 24, padding: mobile ? 28 : 40,
              transform: plan.scale && !mobile ? "scale(1.05)" : "none",
              boxShadow: plan.scale ? "0 16px 64px rgba(20,61,46,0.2)" : "0 1px 4px rgba(0,0,0,0.04)",
              position: "relative", textAlign: "left",
              zIndex: plan.scale ? 2 : 1,
            }}>
              {plan.badge && (
                <div style={{
                  position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)",
                  padding: "6px 16px", borderRadius: 99, background: plan.badgeBg, color: C.white,
                  fontFamily: FF.body, fontWeight: 700, fontSize: 11, letterSpacing: "0.06em", whiteSpace: "nowrap",
                }}>
                  {plan.badge}
                </div>
              )}
              <div style={{ fontFamily: FF.body, fontWeight: 700, fontSize: 18, marginBottom: 8 }}>{plan.name}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: plan.trial ? 8 : 24 }}>
                <span style={{ fontFamily: FF.mono, fontWeight: 500, fontSize: mobile ? 40 : 48 }}>{plan.price}</span>
                <span style={{ fontFamily: FF.body, fontSize: 16, opacity: 0.7 }}>{plan.period}</span>
              </div>
              {plan.trial && (
                <div style={{ fontFamily: FF.body, fontSize: 13, fontWeight: 600, color: plan.bg === C.forest ? "#4ADE80" : C.success, marginBottom: 16 }}>
                  {plan.trial}
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
                {plan.features.map(([included, label], j) => (
                  <div key={j} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {included ? Icon.check(plan.bg === C.forest ? "#4ADE80" : C.success) : Icon.dash(plan.bg === C.forest ? "rgba(253,248,240,0.3)" : C.textSec)}
                    <span style={{ fontFamily: FF.body, fontSize: 14, opacity: included ? 1 : 0.5 }}>{label}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => onLogin({ plan: plan.name.toLowerCase() })} style={{
                width: "100%", padding: "14px 20px", borderRadius: 14, border: "none",
                background: plan.ctaBg, color: plan.ctaColor,
                fontFamily: FF.body, fontWeight: 700, fontSize: 15, cursor: "pointer",
                transition: "all 0.2s",
              }}>
                {plan.ctaLabel}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* Sección: por qué los datos del usuario están seguros */
function SectionSecurity({ mobile }) {
  const fade = useFadeIn();
  const points = [
    {
      icon: Icon.shield,
      title: "No te pedimos la contraseña de tu banco",
      desc: "Tú escribes tus gastos cuando quieras. Nunca tocamos tus cuentas bancarias.",
    },
    {
      icon: Icon.wallet,
      title: "Solo tú ves tus datos",
      desc: "Nunca vendemos tu información ni la compartimos con nadie. Tu información es tuya.",
    },
    {
      icon: Icon.check,
      title: "Te puedes ir cuando quieras",
      desc: "Cancela en un clic. Exporta o borra tus datos cuando se te antoje.",
    },
  ];
  return (
    <section ref={fade.ref} style={{ ...fade.style, background: C.creamDark, padding: mobile ? "80px 20px" : "120px 60px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
        <h2 style={{ fontFamily: FF.display, fontWeight: 600, fontSize: mobile ? 32 : 48, color: C.forest, margin: "0 0 12px" }}>
          Tu dinero está seguro
        </h2>
        <p style={{ fontFamily: FF.body, fontSize: 18, color: C.textSec, margin: "0 0 56px" }}>
          Sabemos que la confianza con tus finanzas no se da gratis. Por eso:
        </p>
        <div style={{
          display: "grid",
          gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr",
          gap: 24,
        }}>
          {points.map((pt, i) => (
            <div key={i} style={{
              background: C.white, borderRadius: 24, padding: 32, textAlign: "left",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)",
            }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: C.cream, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                {/* Ícono de check renderizado a tamaño grande para el tercer punto */}
                {i === 2
                  ? <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.success} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  : pt.icon()
                }
              </div>
              <div style={{ fontFamily: FF.display, fontWeight: 600, fontSize: 18, color: C.forest, marginBottom: 8 }}>
                {pt.title}
              </div>
              <p style={{ fontFamily: FF.body, fontSize: 15, color: C.textSec, lineHeight: 1.6, margin: 0 }}>
                {pt.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SectionFAQ({ faqOpen, toggleFaq }) {
  const fade = useFadeIn();
  const faqs = [
    { q: "Arbo se conecta a mis bancos?", a: "Por ahora, Arbo funciona con registro manual y por voz, asi mantienes el control total de tus datos. Estamos trabajando en conexion directa con bancos mexicanos." },
    { q: "Mis datos estan seguros?", a: "Sí. Tus datos viajan protegidos como en cualquier banco digital, y solo tú puedes verlos. Nunca los vendemos ni los compartimos. Tu información es tuya y punto." },
    { q: "Puedo cambiar de plan despues?", a: "Claro. Puedes subir, bajar o cancelar cuando quieras. Sin penalizaciones." },
    { q: "Funciona para parejas o familias?", a: "Actualmente es individual. Finanzas compartidas están planeado para este año." },
    { q: "Duky es como ChatGPT?", a: "Duky usa IA pero esta especializada en finanzas personales mexicanas. Entiende MSI, quincenas, aguinaldo y toda la onda." },
    { q: "¿Qué son los meses sin intereses (MSI)?", a: "Cuando compras algo a 6, 12 o 24 meses con tu tarjeta de crédito, divides el pago. Arbo te ayuda a llevar el control de cuánto debes, cuántas cuotas faltan y cuándo terminas de pagar." },
    { q: "¿Cómo cancelo mi suscripción?", a: "Entras a Configuración → Suscripción → Cancelar. Listo, sin papeleo, sin llamadas. Tus datos se quedan disponibles hasta el fin del periodo pagado." },
    { q: "¿Qué pasa con mis datos si dejo de usar la app?", a: "Puedes exportar todo en formato Excel cuando quieras, o borrar tu cuenta y todos tus datos en cualquier momento. No retenemos nada que no quieras que retengamos." },
  ];
  return (
    <section ref={fade.ref} style={{ ...fade.style, padding: "80px 20px", background: C.creamDark }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <h2 style={{ fontFamily: FF.display, fontWeight: 600, fontSize: 36, color: C.forest, textAlign: "center", margin: "0 0 40px" }}>
          Preguntas frecuentes
        </h2>
        {faqs.map((faq, i) => (
          <div key={i} style={{ borderBottom: `1px solid ${C.divider}` }}>
            <button
              onClick={() => toggleFaq(i)}
              style={{
                width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "20px 0", background: "none", border: "none", cursor: "pointer", textAlign: "left",
              }}
            >
              <span style={{ fontFamily: FF.body, fontWeight: 600, fontSize: 16, color: C.text, flex: 1, paddingRight: 16 }}>{faq.q}</span>
              <div style={{
                width: 28, height: 28, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                transform: faqOpen[i] ? "rotate(45deg)" : "rotate(0deg)", transition: "transform 0.3s ease",
              }}>
                {Icon.plus(C.forest)}
              </div>
            </button>
            <div style={{
              maxHeight: faqOpen[i] ? 300 : 0, overflow: "hidden",
              transition: "max-height 0.3s ease",
            }}>
              <p style={{ fontFamily: FF.body, fontSize: 15, color: C.textSec, lineHeight: 1.7, margin: "0 0 20px", paddingRight: 44 }}>
                {faq.a}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionCTA({ mobile, onLogin }) {
  const fade = useFadeIn();
  return (
    <section ref={fade.ref} style={{ ...fade.style, background: C.forest, padding: mobile ? "80px 20px" : "120px 60px", textAlign: "center" }}>
      <h2 style={{ fontFamily: FF.display, fontWeight: 700, fontSize: mobile ? 36 : 56, color: C.textInv, lineHeight: 1.1, margin: "0 0 16px" }}>
        Tu yo del futuro<br />te va a agradecer.
      </h2>
      <p style={{ fontFamily: FF.body, fontSize: 18, color: "rgba(253,248,240,0.7)", margin: "0 0 36px" }}>
        Empieza hoy. Es gratis y toma menos de un minuto.
      </p>
      <button onClick={onLogin} style={{
        padding: "18px 40px", borderRadius: 16, border: "none", background: C.orange, color: C.white,
        fontFamily: FF.body, fontWeight: 700, fontSize: 18, cursor: "pointer",
        boxShadow: "0 4px 24px rgba(198,93,59,0.35)", transition: "all 0.2s",
      }}>
        Crear mi cuenta gratis
      </button>
    </section>
  );
}

function Footer({ mobile }) {
  return (
    <footer style={{ background: C.footerBg, padding: mobile ? "40px 20px" : "60px 60px" }}>
      <div style={{
        maxWidth: 1100, margin: "0 auto",
        display: "grid", gridTemplateColumns: mobile ? "1fr" : "2fr 1fr 1fr 1fr", gap: mobile ? 32 : 40,
      }}>
        <div>
          <div style={{ fontFamily: FF.display, fontWeight: 700, fontSize: 24, color: C.textInv, marginBottom: 8 }}>arbo</div>
          <p style={{ fontFamily: FF.body, fontSize: 14, color: "rgba(253,248,240,0.5)", margin: 0, lineHeight: 1.6 }}>Tu compañera de finanzas personales.</p>
        </div>
        <div>
          <div style={{ fontFamily: FF.body, fontWeight: 700, fontSize: 13, color: "rgba(253,248,240,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Producto</div>
          <a href="#features" style={footerLink}>Funciones</a>
          <a href="#precios" style={footerLink}>Precios</a>
        </div>
        <div>
          <div style={{ fontFamily: FF.body, fontWeight: 700, fontSize: 13, color: "rgba(253,248,240,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Legal</div>
          <a href="/terminos-y-condiciones.md" target="_blank" rel="noopener noreferrer" style={footerLink}>Terminos</a>
          <a href="/aviso-de-privacidad.md" target="_blank" rel="noopener noreferrer" style={footerLink}>Privacidad</a>
        </div>
        <div>
          <div style={{ fontFamily: FF.body, fontWeight: 700, fontSize: 13, color: "rgba(253,248,240,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Contacto</div>
          <a href="mailto:hola@arbo.digital" style={footerLink}>hola@arbo.digital</a>
        </div>
      </div>
      <div style={{
        maxWidth: 1100, margin: "32px auto 0", paddingTop: 24, borderTop: "1px solid rgba(253,248,240,0.1)",
        fontFamily: FF.body, fontSize: 13, color: "rgba(253,248,240,0.35)", textAlign: "center",
      }}>
        &copy; 2026 Arbo &middot; Hecho en México
      </div>
    </footer>
  );
}

const footerLink = {
  display: "block", fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "rgba(253,248,240,0.6)",
  textDecoration: "none", marginBottom: 8, transition: "color 0.2s",
};
